'use client'

import { CSSProperties, ReactNode, useEffect, useRef } from 'react'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'

import Navigation from 'composition/navigation/Navigation'
import { useRibbon } from 'modules/content'
import {
  FetchUpsellingResponse,
  NavMenuCategory,
  ProductTypes
} from 'modules/products'
import { theme } from 'shared/ui/utils/designTokens'
import { getNavHeight } from 'shared/ui/utils/layout'
import { isHeaderMinimalistic } from 'shared/utils/routes'

import styles from './MainLayout.module.scss'

const Footer = dynamic(() => import('composition/footer/Footer'))

const MainLayout = ({
  children,
  promotedProducts,
  categories,
  menuPromotionItems,
  isFullLayout = false
}: {
  children: ReactNode
  promotedProducts: ProductTypes[]
  categories: NavMenuCategory[]
  menuPromotionItems: FetchUpsellingResponse['data'] | null
  isFullLayout?: boolean
}) => {
  const pathname = usePathname()
  const minimalisticHeader = isHeaderMinimalistic(pathname || '')
  const { ribbon } = useRibbon()
  const isPopStateRef = useRef(false)
  const isFirstRenderRef = useRef(true)

  useEffect(() => {
    const handlePopState = () => {
      isPopStateRef.current = true
    }

    window.addEventListener('popstate', handlePopState)

    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    // App Router sometimes skips its default scroll-to-top when the new page
    // shares the same route template (e.g. category -> product both render
    // via [...entity]/page.tsx). Force it, but not in the cases below

    // Initial load: the browser has positioned the viewport itself
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false

      return
    }

    // Back/forward - the browser restores the previous scroll position.
    if (isPopStateRef.current) {
      isPopStateRef.current = false

      return
    }

    // Navigation to a fragment target must land on the anchor, not the top.
    if (window.location.hash) return

    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <>
      <Navigation
        isMinimalistHeader={minimalisticHeader}
        promotedProducts={promotedProducts}
        categories={categories}
        menuPromotionItems={menuPromotionItems}
      />
      <AppContent
        $isMinimalistHeader={minimalisticHeader}
        $isRibbonVisible={!!ribbon}
        $isFullLayout={isFullLayout}
        id='main'
      >
        {children}
      </AppContent>
      <Footer isPaymentProccesPage={minimalisticHeader} />
      {/* For now in Lokalny Rolnik we don't promote app */}
    </>
  )
}

export default MainLayout

const AppContent = ({
  $isMinimalistHeader = false,
  $isRibbonVisible = false,
  $isFullLayout = false,
  id,
  children
}: {
  $isMinimalistHeader?: boolean
  $isRibbonVisible?: boolean
  $isFullLayout?: boolean
  id?: string
  children?: ReactNode
}) => {
  const isRibbonVisible = !!$isRibbonVisible

  const navHeights = {
    '--app-content-nav-desktop': `${getNavHeight({
      ribbonHeight: theme.dimensions.ribbonHeightDesktop,
      topNavHeight: theme.dimensions.navHeightDesktop,
      isRibbonVisible
    })}px`,
    '--app-content-nav-tablet': `${getNavHeight({
      ribbonHeight: theme.dimensions.ribbonHeightTablet,
      topNavHeight: $isMinimalistHeader
        ? theme.dimensions.topNavMinimalistic
        : theme.dimensions.navHeightTablet,
      isRibbonVisible
    })}px`,
    '--app-content-nav-mobile': `${getNavHeight({
      ribbonHeight: theme.dimensions.ribbonHeightMobile,
      topNavHeight: theme.dimensions.navHeightMobile,
      isRibbonVisible
    })}px`
  } as CSSProperties

  return (
    <main
      id={id}
      className={clsx(styles.root, {
        [styles.minimalistHeader]: $isMinimalistHeader,
        [styles.fullLayout]: $isFullLayout
      })}
      style={navHeights}
    >
      {children}
    </main>
  )
}
